import { playlistCardTemplate } from '../carousel/carousel-templates';
import { initCarousel } from '../carousel/init-carousel';
import { initPlayers } from '../video-player/init-players';
import { getLabel } from '../../labels/labelling-service';
import { VIDEO_PLAYER_STATUS } from '../video-player/constants';
import { removeElementDom } from '../../utils/dom-utils';
import { includes } from '../../utils/array-utils';
import { isStickyCarouselTemplateEnable } from '../../utils/aem-utils';
import { stickyCarousel } from '../video-player/sticky-carousel';
import logger from '../../services/logging/logging-service';

const config = require('config');

const VIDEO_CARD_CLASS_NAME = 'c-playlist-carousel c-video-card';
const VIDEO_CARD_IMAGE_CONTAINER_CLASS_NAME = 'c-video-card__img-container';
const PLAYER_CAROUSEL_ID = 'j-player-with-carousel';
const PLAYER_CAROUSEL_CLASS_NAME = 'j-player-carousel';
const DATA_VIDEO_ID = 'data-videoid';
const ACTIVE_CARD_CLASS_NAME = 'c-playlist-carousel c-video-card c-video-card--offset is-active';
const NEXT_CARD_ACTIVE_CLASS_NAME = 'c-playlist-carousel c-video-card  is-loading';
const PLAYLIST_CAROUSEL_CONTAINER_CLASS_NAME = 'c-playlist-carousel__container';
const PLAYLIST_CONTAINER = 'video-with-carousel-section-container';
const PROGRESS_BAR_CLASS_NAME = 'c-video-card__progress-bar';

const PROGRESS_TIMER_INTERVAL = config.videoPlaylist.progressTimerInterval || 50;
const VIDEO_WAIT_TIMEOUT = config.videoPlaylist.videoWaitTimeout || 5000;

const DEFAULT_PER_PAGE_ELEMENTS = 3;

// DOM Elements Refs used:
const $playlistPlayerContainer = document.querySelector(`#${PLAYER_CAROUSEL_ID}`);
const $playlistCarouselContainer = document.getElementsByClassName(PLAYLIST_CAROUSEL_CONTAINER_CLASS_NAME)[0];
const $playerContainer = document.getElementsByClassName(PLAYER_CAROUSEL_CLASS_NAME)[0];
const $videoCard = document.getElementsByClassName(VIDEO_CARD_CLASS_NAME);
const $playlistContainer = document.querySelector(`#${PLAYLIST_CONTAINER}`);

const getVideoId = (slideIndex) => {
  return $videoCard[slideIndex].dataset.videoid;
};

const getProgressBar = (activeCard) => activeCard.getElementsByClassName(PROGRESS_BAR_CLASS_NAME)[0];

export const hideVideoPlayer = () => {
  $playlistPlayerContainer.style.display = 'none';
};

export const showVideoPlayer = () => {
  $playlistPlayerContainer.style.display = 'block';
};

export const removePlaylistContainer = () => {
  const containerClasses = $playlistContainer.classList;
  const editmode = includes('j-editmode')(containerClasses);

  if (!editmode) {
    const gridContainerRef = includes('l-grid')($playlistContainer.parentNode.parentNode.className)
      ? $playlistContainer.parentNode.parentNode
      : null;
    const playlistContainerHasSiblings = $playlistContainer.nextElementSibling !== null;

    removeElementDom($playlistContainer);
    if (!playlistContainerHasSiblings && gridContainerRef) {
      gridContainerRef.classList.remove('l-offset');
    }
  }
};

const destroyCarousel = (carouselId) => {
  const carousel = document.querySelector(`#${carouselId}`);
  if (carousel) {
    if ($playlistCarouselContainer.hasChildNodes()) {
      $playlistCarouselContainer.removeChild(carousel);
    }
  }
};

const createNewCarouselElement = (carouselId) => {
  const carousel = document.createElement('div');
  carousel.setAttribute('id', carouselId);
  carousel.setAttribute('class', carouselId);
  $playlistCarouselContainer.appendChild(carousel);
};

const resetPlayerListeners = (
  player,
  events = [VIDEO_PLAYER_STATUS.READY, VIDEO_PLAYER_STATUS.COMPLETE, VIDEO_PLAYER_STATUS.REPLAY_CLICK]
) => {
  for (const event of events) {
    player.off(event);
  }
};

const getIndexFromAssetId = (elementIds, assetId) => {
  return elementIds.findIndex((element) => element === assetId);
};

export const computePerPageElementsByCarouselWidth = () => {
  if ($playerContainer) {
    const widgetWidth = $playerContainer.clientWidth;
    const perPage = {};
    if (widgetWidth >= 800) {
      perPage[800] = 4.5; //  4 + 1/2  items for carousel width wider than 800px
    } else if (widgetWidth >= 600) {
      perPage[600] = 3; // 3 + 1/2  items for carousel width wider than 300px
    } else {
      perPage[300] = 2.5;
    }
    return perPage;
  }
  return DEFAULT_PER_PAGE_ELEMENTS;
};

const filterVideoCarouselPlayer = (players) => {
  const { playerList, playerConfigList } = players;
  const playerWithCarouselIndex = playerConfigList.findIndex((player) => player.containerid === PLAYER_CAROUSEL_ID);
  return playerList[playerWithCarouselIndex];
};

const playVideo = (videoId) => {
  $playerContainer.setAttribute(DATA_VIDEO_ID, videoId);
  const players = initPlayers();
  return filterVideoCarouselPlayer(players);
};

const playNextVideoIndex = (videindex) => {
  const videoId = getVideoId(videindex);
  playVideo(videoId);
};

const onVideoReady = (videoIndex) => {
  const activeCard = $videoCard[videoIndex];
  activeCard.className = ACTIVE_CARD_CLASS_NAME;
};

const animateComponent = (element) => {
  const progressBarIncrement = 100 / (VIDEO_WAIT_TIMEOUT / PROGRESS_TIMER_INTERVAL);
  element.value += progressBarIncrement;
};

const cleanActiveCard = (activeCardIndex) => {
  const activeCard = $videoCard[activeCardIndex];
  const videoCardImageContainer = activeCard.getElementsByClassName(VIDEO_CARD_IMAGE_CONTAINER_CLASS_NAME)[0];
  const activeCardProgressBar = getProgressBar(activeCard);
  activeCard.className = VIDEO_CARD_CLASS_NAME;
  videoCardImageContainer.removeAttribute('data-next-video');
  activeCardProgressBar.value = 0;
  activeCardProgressBar.style.display = 'none';
};

const setActiveCard = (cardNavigator) => {
  const card = $videoCard[cardNavigator];
  const videoCardImageContainer = card.getElementsByClassName(VIDEO_CARD_IMAGE_CONTAINER_CLASS_NAME)[0];
  const progressBar = getProgressBar(card);
  card.className = NEXT_CARD_ACTIVE_CLASS_NAME;
  videoCardImageContainer.setAttribute('data-next-video', getLabel('video.playlist.next.video'));
  progressBar.style.display = 'block';
  return progressBar;
};

const setActiveCardOnReplay = (cardNavigator) => {
  const card = $videoCard[cardNavigator];
  card.className = ACTIVE_CARD_CLASS_NAME;
};

const setPlayerInAutoplay = (playerid, isAutostart) => {
  const currentPlayerContainer = document.querySelector(`#${playerid}`);
  if (currentPlayerContainer.dataset.autostart !== isAutostart) {
    currentPlayerContainer.dataset.autostart = isAutostart;
  }
};

const onVideoComplete = (nextVideoIndex, carousel) => {
  setPlayerInAutoplay(PLAYER_CAROUSEL_ID, true);
  cleanActiveCard(nextVideoIndex - 1);
  const progressBar = setActiveCard(nextVideoIndex);
  const progressTimerIntervalId = setInterval(animateComponent, PROGRESS_TIMER_INTERVAL, progressBar);
  const videoWaitTimeoutId = setTimeout(() => {
    playNextVideoIndex(nextVideoIndex);
    carousel.jumpToSlide(nextVideoIndex);
    progressBar.style.display = 'none';
    clearInterval(progressTimerIntervalId);
  }, VIDEO_WAIT_TIMEOUT);
  return {
    progressTimerIntervalId,
    videoWaitTimeoutId,
  };
};

const stopVideoCompletedAnimations = (progressTimerIntervalId, videoWaitTimeoutId) => {
  if (progressTimerIntervalId && videoWaitTimeoutId) {
    clearTimeout(videoWaitTimeoutId);
    clearInterval(progressTimerIntervalId);
  }
};

export const preparePlaylistPlayer = () => {
  $playlistPlayerContainer.classList.add('j-player');
};

/**
 * Initialize new Video Playlist
 * @param {Array}  data the model for video carousel playlist
 * @param {String} carouselId The Id to use as a new carouselId.
 */
export const initVideoPlaylist = (data, carouselId) => {
  const carouselData = [...data];
  let currentVideoIndex = 0;
  let carouselEndReached = false;
  let timers = {
    progressTimerIntervalId: undefined,
    videoWaitTimeoutIdId: undefined,
  };
  const dataIds = carouselData.map((element) => element.assetId);

  const onCardClicked = (metadata) => {
    setPlayerInAutoplay(PLAYER_CAROUSEL_ID, true);
    const assetId = metadata.assetId;
    const slideIndex = getIndexFromAssetId(dataIds, assetId);
    if (currentVideoIndex !== slideIndex) {
      slideIndex === dataIds.length - 1 ? (carouselEndReached = true) : (carouselEndReached = false);
      cleanActiveCard(currentVideoIndex);
      // eslint-disable-next-line no-use-before-define
      carousel.jumpToSlide(slideIndex);
      stopVideoCompletedAnimations(timers.progressTimerIntervalId, timers.videoWaitTimeoutId);
      playVideo(metadata.assetId);
      currentVideoIndex = slideIndex;
    } else {
      // TODO: click on the same slide you're playing
    }
  };
  createNewCarouselElement(carouselId);
  config.videoPlaylist.carouselConfig.perPage = computePerPageElementsByCarouselWidth();

  const carousel = initCarousel(
    carouselId,
    carouselData,
    playlistCardTemplate,
    onCardClicked,
    config.videoPlaylist.carouselConfig
  );

  const player = playVideo(getVideoId(currentVideoIndex));

  if (isStickyCarouselTemplateEnable()) {
    logger.info('[ STICKY - CAROUSEL ] initialize sticky for carousel');
    stickyCarousel(player);
  }

  player.on(VIDEO_PLAYER_STATUS.READY, () => onVideoReady(currentVideoIndex));

  player.on(VIDEO_PLAYER_STATUS.COMPLETE, () => {
    if (currentVideoIndex < dataIds.length - 1) {
      currentVideoIndex++;
      timers = onVideoComplete(currentVideoIndex, carousel);
      carouselEndReached = false;
    } else {
      carouselEndReached = true;
    }
  });

  player.on(VIDEO_PLAYER_STATUS.REPLAY_CLICK, () => {
    if (!carouselEndReached) {
      stopVideoCompletedAnimations(timers.progressTimerIntervalId, timers.videoWaitTimeoutId);
      cleanActiveCard(currentVideoIndex);
      currentVideoIndex--;
      setActiveCardOnReplay(currentVideoIndex);
    }
  });

  return {
    destroy: (carouselIdToDestroy) => {
      stopVideoCompletedAnimations(timers.progressTimerIntervalId, timers.videoWaitTimeoutId);
      resetPlayerListeners(player);
      destroyCarousel(carouselIdToDestroy);
      carousel.destroy();
    },
    pause: () => player.pause(),
    play: () => player.play(),
  };
};
